// Parser for CONDITIONS_SPORT_REVISIONS and CONDITIONS_SPORT_SHADOW_REVISIONS (design sections
// 7.5 and 8.4). Pure: callers pass the raw values; nothing here reads the environment.
//
// Grammar (both flags): comma-separated `sport=revision` entries, for example
// `surf=2,bodyboarding=2`. `sport` is an exact non-ski SPORT_SCORING id (no aliases); `revision`
// is a decimal integer from 1 to 32767 without sign or leading zeros. The publish flag lists each
// sport at most once; the shadow flag may list several distinct revisions for one sport, none
// equal to that sport's publish revision. At runtime whitespace around entries and around `=` is
// ignored and empty entries (a trailing comma) are skipped, the same tolerance as Track A's
// SCORE_STORE_READER_SPORTS, so a stray space or newline in a deployed value can never change
// its meaning or freeze every family. scripts/check-sport-revisions.ts refuses any value that is
// not in exact canonical spelling, so what is deployed is exact (a deviation from the design's
// "exact values only" wording, recorded in the design's WP0 notes for the owner to confirm).
//
// Fail closed per family. An entry that names a known sport but is invalid (bad number,
// duplicate, undeclared revision, or a revision this build cannot run, which includes any revision
// other than publish 1 while the family's writers are not wired, core/availability.ts
// FAMILY_ENGINE_WIRED) invalidates that sport's family. An entry that names no engine sport (a typo, a ski sport) cannot be
// attributed, so it invalidates every family. An invalid family keeps the default publish
// revision (1) for readers, runs no engine targets, and stops its legacy writes (design 8.4;
// for MTB that includes the ski rows it shares). Readers and writers apply the same
// availability rule (core/availability.ts; the default here), so a revision this build cannot
// produce fails closed for both. scripts/check-sport-revisions.ts runs this parser with the
// registry before any deployment changes either flag.
import { ENGINE_FAMILIES, ENGINE_FAMILY_BY_SPORT, ENGINE_SPORTS_BY_FAMILY, isEngineSport } from "./sports";
import { manifestRevisionAvailable, type RevisionAvailability } from "./availability";
import { LEGACY_REVISION, MAX_REVISION, isRevisionDeclared } from "./revisions";
import type { EngineFamily, EngineSport } from "./types";

export type { RevisionAvailability } from "./availability";

export const SPORT_REVISIONS_FLAG = "CONDITIONS_SPORT_REVISIONS";
export const SPORT_SHADOW_REVISIONS_FLAG = "CONDITIONS_SPORT_SHADOW_REVISIONS";

export type RevisionIssue = Readonly<{
  flag: "publish" | "shadow";
  /** The family the entry invalidates, or "all" when it names no engine sport. */
  scope: EngineFamily | "all";
  entry: string;
  reason: string;
}>;

export type FamilyRevisionMode = Readonly<{
  family: EngineFamily;
  /** Both flags valid for this family: engine targets and legacy writes may run. */
  valid: boolean;
  publishValid: boolean;
  shadowValid: boolean;
  /** Explicitly listed publish revisions (empty when the publish segment is invalid). */
  publish: Readonly<Partial<Record<EngineSport, number>>>;
  /** Listed shadow revisions, ascending (empty when the family is invalid). */
  shadow: Readonly<Partial<Record<EngineSport, readonly number[]>>>;
  issues: readonly RevisionIssue[];
}>;

export type RevisionMode = Readonly<{
  publishRaw: string;
  shadowRaw: string;
  families: Readonly<Record<EngineFamily, FamilyRevisionMode>>;
  issues: readonly RevisionIssue[];
}>;

const REVISION_TOKEN = /^[1-9][0-9]{0,4}$/u;

type ParsedEntry = Readonly<{ entry: string; sport: EngineSport | null; revision: number | null; problem: string | null }>;

function splitEntries(raw: string | undefined): string[] {
  return (raw ?? "").split(",").map((entry) => entry.trim()).filter((entry) => entry.length > 0);
}

function parseEntry(entry: string): ParsedEntry {
  const eq = entry.indexOf("=");
  const sportToken = (eq < 0 ? entry : entry.slice(0, eq)).trim();
  const revisionToken = eq < 0 ? "" : entry.slice(eq + 1).trim();
  if (!isEngineSport(sportToken)) {
    return { entry, sport: null, revision: null, problem: `"${sportToken}" is not a non-ski sport id` };
  }
  if (!REVISION_TOKEN.test(revisionToken) || Number(revisionToken) > MAX_REVISION) {
    return { entry, sport: sportToken, revision: null, problem: `revision must be an integer from 1 to ${MAX_REVISION}` };
  }
  return { entry, sport: sportToken, revision: Number(revisionToken), problem: null };
}

function availabilityProblem(
  sport: EngineSport,
  revision: number,
  use: "publish" | "shadow",
  available: RevisionAvailability,
): string | null {
  if (!isRevisionDeclared(sport, revision)) return `revision ${revision} is not declared for ${sport} (engines/core/revisions.ts)`;
  if (!available(sport, revision, use)) {
    return `revision ${revision} of ${sport} has no ${use} implementation in this build (its modules, or its family's wired writers: core/availability.ts)`;
  }
  return null;
}

type MutableFamily = {
  publish: Partial<Record<EngineSport, number>>;
  shadow: Partial<Record<EngineSport, number[]>>;
  publishIssues: RevisionIssue[];
  shadowIssues: RevisionIssue[];
};

function emptyFamilies(): Record<EngineFamily, MutableFamily> {
  return Object.fromEntries(ENGINE_FAMILIES.map((family) => [family, {
    publish: {}, shadow: {}, publishIssues: [], shadowIssues: [],
  }])) as unknown as Record<EngineFamily, MutableFamily>;
}

/**
 * Parses both revision flags. `available` defaults to this build's manifest
 * (core/availability.ts manifestRevisionAvailable, equal to registry.ts revisionAvailable), so
 * every parser fails closed on a revision the build cannot produce. Grammar tests pass their own
 * predicate.
 */
export function parseRevisionMode(
  values: Readonly<{ publish?: string | undefined; shadow?: string | undefined }>,
  options: Readonly<{ available?: RevisionAvailability }> = {},
): RevisionMode {
  const available = options.available ?? manifestRevisionAvailable;
  const families = emptyFamilies();
  const globalIssues: RevisionIssue[] = [];
  const issue = (flag: "publish" | "shadow", scope: EngineFamily | "all", entry: string, reason: string): void => {
    const record = Object.freeze({ flag, scope, entry, reason });
    if (scope === "all") globalIssues.push(record);
    else (flag === "publish" ? families[scope].publishIssues : families[scope].shadowIssues).push(record);
  };

  for (const parsed of splitEntries(values.publish).map(parseEntry)) {
    if (parsed.sport === null) { issue("publish", "all", parsed.entry, parsed.problem ?? "unknown sport"); continue; }
    const family = ENGINE_FAMILY_BY_SPORT[parsed.sport];
    if (parsed.revision === null) { issue("publish", family, parsed.entry, parsed.problem ?? "invalid revision"); continue; }
    if (Object.hasOwn(families[family].publish, parsed.sport)) {
      issue("publish", family, parsed.entry, `${parsed.sport} is listed more than once`);
      continue;
    }
    const problem = availabilityProblem(parsed.sport, parsed.revision, "publish", available);
    if (problem) { issue("publish", family, parsed.entry, problem); continue; }
    families[family].publish[parsed.sport] = parsed.revision;
  }

  const publishAllInvalid = globalIssues.some((entry) => entry.flag === "publish");
  const publishValidFor = (family: EngineFamily): boolean => !publishAllInvalid && families[family].publishIssues.length === 0;
  const publishFor = (sport: EngineSport): number => {
    const family = ENGINE_FAMILY_BY_SPORT[sport];
    return publishValidFor(family) ? families[family].publish[sport] ?? LEGACY_REVISION : LEGACY_REVISION;
  };

  for (const parsed of splitEntries(values.shadow).map(parseEntry)) {
    if (parsed.sport === null) { issue("shadow", "all", parsed.entry, parsed.problem ?? "unknown sport"); continue; }
    const family = ENGINE_FAMILY_BY_SPORT[parsed.sport];
    if (parsed.revision === null) { issue("shadow", family, parsed.entry, parsed.problem ?? "invalid revision"); continue; }
    const listed = families[family].shadow[parsed.sport] ?? [];
    if (listed.includes(parsed.revision)) {
      issue("shadow", family, parsed.entry, `${parsed.sport}=${parsed.revision} is listed more than once`);
      continue;
    }
    if (parsed.revision === publishFor(parsed.sport)) {
      issue("shadow", family, parsed.entry, `revision ${parsed.revision} is ${parsed.sport}'s publish revision`);
      continue;
    }
    const problem = availabilityProblem(parsed.sport, parsed.revision, "shadow", available);
    if (problem) { issue("shadow", family, parsed.entry, problem); continue; }
    families[family].shadow[parsed.sport] = [...listed, parsed.revision];
  }

  const shadowAllInvalid = globalIssues.some((entry) => entry.flag === "shadow");
  const frozenFamilies = Object.fromEntries(ENGINE_FAMILIES.map((family) => {
    const state = families[family];
    const publishValid = publishValidFor(family);
    const shadowValid = !shadowAllInvalid && state.shadowIssues.length === 0;
    const valid = publishValid && shadowValid;
    const shadow = valid
      ? Object.fromEntries(ENGINE_SPORTS_BY_FAMILY[family]
        .filter((sport) => state.shadow[sport]?.length)
        .map((sport) => [sport, Object.freeze([...(state.shadow[sport] ?? [])].sort((a, b) => a - b))]))
      : {};
    return [family, Object.freeze({
      family,
      valid,
      publishValid,
      shadowValid,
      publish: Object.freeze(publishValid ? { ...state.publish } : {}),
      shadow: Object.freeze(shadow),
      issues: Object.freeze([...globalIssues, ...state.publishIssues, ...state.shadowIssues]),
    } satisfies FamilyRevisionMode)];
  })) as unknown as Record<EngineFamily, FamilyRevisionMode>;

  return Object.freeze({
    publishRaw: values.publish ?? "",
    shadowRaw: values.shadow ?? "",
    families: Object.freeze(frozenFamilies),
    issues: Object.freeze([
      ...globalIssues,
      ...ENGINE_FAMILIES.flatMap((family) => [...families[family].publishIssues, ...families[family].shadowIssues]),
    ]),
  });
}

/** The mode with both flags unset: every sport publishes revision 1 and nothing shadows. */
export const DEFAULT_REVISION_MODE: RevisionMode = parseRevisionMode({});

/**
 * Publish revision of a non-ski sport under a parsed mode: the listed revision while its
 * family's publish segment is valid, otherwise revision 1 (the default, also the fail-closed
 * value for readers).
 */
export function publishRevisionFor(mode: RevisionMode, sport: EngineSport): number {
  const family = mode.families[ENGINE_FAMILY_BY_SPORT[sport]];
  return family.publishValid ? family.publish[sport] ?? LEGACY_REVISION : LEGACY_REVISION;
}

/** Shadow revisions of a sport; empty unless its family's whole revision mode is valid. */
export function shadowRevisionsFor(mode: RevisionMode, sport: EngineSport): readonly number[] {
  const family = mode.families[ENGINE_FAMILY_BY_SPORT[sport]];
  return family.valid ? family.shadow[sport] ?? [] : [];
}

/** True when the family's engine targets and legacy writes may run (design 8.4). */
export function familyRevisionModeValid(mode: RevisionMode, family: EngineFamily): boolean {
  return mode.families[family].valid;
}
